import { Link } from 'react-router-dom';
import type { EncaissementsVoiture, PartMois } from '../api/types';
import { Bandeau } from './ui';
import { montant } from '../lib/format';
import { T } from '../i18n';

/**
 * Quand l'argent d'une location n'entre pas dans le mois de la location.
 *
 * La règle : l'argent se compte dans le mois où il a été REÇU. Une location
 * d'octobre payée en septembre est donc dans la caisse de septembre. Mais sans
 * un mot, chacun des deux mois paraît faux — septembre a « trop » d'argent pour
 * ses locations, octobre a des locations « sans » argent. Ces notes disent,
 * dans les deux mois, où est passé l'argent.
 *
 * Deux précisions qui évitent de chercher de l'argent qui n'est pas là :
 *   - un chèque ou un virement part à la banque, pas dans la caisse du mois ;
 *   - une seule location s'annonce au singulier — le gérant ne doit pas en
 *     chercher plusieurs.
 */

/** « octobre », ou « octobre 2027 » quand l'année diffère du mois affiché. */
export function moisLisible(mois: string, reference: string): string {
  const [a, m] = mois.split('-').map(Number);
  const nom = new Date(a!, (m ?? 1) - 1, 1).toLocaleDateString(T.decalageEncaissement.localeMois, { month: 'long' });
  return mois.slice(0, 4) === reference.slice(0, 4) ? nom : `${nom} ${a}`;
}

const somme = (l: PartMois[]) => l.reduce((t, p) => t + p.montantMinor, 0);

/** « une location d'octobre », « 3 locations de septembre ». */
const locationsDe = (p: PartMois, reference: string) =>
  T.decalageEncaissement.locationsDuMois(p.dossiers.length, moisLisible(p.mois, reference));

/**
 * Où se trouve l'argent reçu tel mois : dans la caisse, à la banque, ou les deux.
 * « dans la caisse de septembre » / « à la banque (reçus en septembre) » /
 * « dans la caisse de septembre, dont 15 200,00 DZD à la banque ».
 */
function ouEstLArgent(p: PartMois, reference: string): string {
  const nom = moisLisible(p.mois, reference);
  const t = T.decalageEncaissement.ou;
  if (p.banqueMinor === 0) return t.caisse(nom);
  if (p.banqueMinor === p.montantMinor) return t.banque(nom);
  return t.mixte(nom, montant(p.banqueMinor));
}

export const aDesDecalages = (e: EncaissementsVoiture) =>
  e.avance.length + e.retard.length + e.dejaEncaisse.length + e.encaissePlusTard.length > 0
  || e.surAnnuleesMinor !== 0;

/* ==================== Sur la carte de la voiture ==================== */

/** Les petites notes sous le montant encaissé de la carte. Une ligne par mois concerné. */
export function NotesDecalage({ e, mois }: { e: EncaissementsVoiture; mois: string }) {
  if (!aDesDecalages(e) && e.encaisseBanqueMinor === 0) return null;
  const t = T.decalageEncaissement.notes;
  return (
    <div className="rv-notes">
      {e.avance.map((p) => (
        <div key={`a${p.mois}`} className="rv-note" style={{ color: 'var(--violet)' }}>
          {t.dont}<b>{montant(p.montantMinor)}</b>{t.avance(locationsDe(p, mois))}
        </div>
      ))}
      {e.retard.map((p) => (
        <div key={`r${p.mois}`} className="rv-note sourdine">
          {t.dont}<b>{montant(p.montantMinor)}</b>{t.retard(locationsDe(p, mois))}
        </div>
      ))}
      {e.surAnnuleesMinor !== 0 && (
        <div className="rv-note sourdine">
          {t.dont}<b>{montant(e.surAnnuleesMinor)}</b>{t.annulee}
        </div>
      )}
      {e.encaisseBanqueMinor !== 0 && (
        <div className="rv-note sourdine">
          {t.dont}<b>{montant(e.encaisseBanqueMinor)}</b>{t.banque}
        </div>
      )}
      {e.dejaEncaisse.map((p) => (
        <div key={`d${p.mois}`} className="rv-note rv-note-rappel">
          {t.dejaAvant(moisLisible(p.mois, mois), p.dossiers.length)}
          <b>{montant(p.montantMinor)}</b>{t.dejaApres(ouEstLArgent(p, mois))}
        </div>
      ))}
      {e.encaissePlusTard.map((p) => (
        <div key={`t${p.mois}`} className="rv-note sourdine">
          {t.plusTardAvant(moisLisible(p.mois, mois), p.dossiers.length)}
          <b>{montant(p.montantMinor)}</b>{t.plusTardApres(ouEstLArgent(p, mois))}
        </div>
      ))}
    </div>
  );
}

/* ==================== Sur la fiche détaillée de la voiture ==================== */

function ListeDossiers({ p }: { p: PartMois }) {
  return (
    <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
      {p.dossiers.map((d) => (
        <li key={d.reservationId}>
          <Link to={`/reservations/${d.reservationId}`}>{d.numero}</Link> · {d.client} —{' '}
          <b>{montant(d.montantMinor)}</b>
          {d.banqueMinor > 0 && <span className="sourdine">{T.decalageEncaissement.dontBanque(montant(d.banqueMinor))}</span>}
        </li>
      ))}
    </ul>
  );
}

/**
 * Les mêmes rappels, en grand, avec les dossiers derrière chaque montant.
 *
 * Deux boutons, parce que ce sont deux questions : « et cette voiture, ce
 * mois-là ? » reste sur la fiche ; « et la caisse de ce mois-là ? » ouvre la
 * caisse du mois, toutes voitures confondues.
 */
export function BandeauxDecalage({ e, mois, versMois }:
  { e: EncaissementsVoiture; mois: string; versMois: (m: string) => void }) {
  if (!aDesDecalages(e)) return null;
  const t = T.decalageEncaissement;
  const boutons = (m: string) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
      <button className="btn btn-s" onClick={() => versMois(m)}>
        {t.voirVoitureEn(moisLisible(m, mois))}
      </button>
      <Link className="btn btn-s" to={`/caisse?mois=${m}`}>
        {t.ouvrirCaisseDe(moisLisible(m, mois))}
      </Link>
    </div>
  );
  const plusieurs = (l: PartMois[]) => l.reduce((t, p) => t + p.dossiers.length, 0) > 1;

  return (
    <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
      {e.avance.length > 0 && (
        <Bandeau type="accent" titre={t.avance.titre(montant(somme(e.avance)))}>
          {t.avance.texte(plusieurs(e.avance))}
          {e.avance.map((p) => (
            <div key={p.mois} style={{ marginTop: 8 }}>
              {t.pour(locationsDe(p, mois))}<b>{montant(p.montantMinor)}</b>
              <ListeDossiers p={p} />
              {boutons(p.mois)}
            </div>
          ))}
        </Bandeau>
      )}

      {e.dejaEncaisse.length > 0 && (
        <Bandeau type="info" titre={t.deja.titre(montant(somme(e.dejaEncaisse)))}>
          {t.deja.texte(plusieurs(e.dejaEncaisse))}
          {e.dejaEncaisse.map((p) => (
            <div key={p.mois} style={{ marginTop: 8 }}>
              {t.encaissesEn(moisLisible(p.mois, mois))}<b>{montant(p.montantMinor)}</b>
              {t.virguleOu(ouEstLArgent(p, mois))}
              <ListeDossiers p={p} />
              {boutons(p.mois)}
            </div>
          ))}
        </Bandeau>
      )}

      {e.retard.length > 0 && (
        <Bandeau type="info" titre={t.retard.titre(montant(somme(e.retard)))}>
          {e.retard.map((p) => (
            <div key={p.mois} style={{ marginTop: 8 }}>
              {t.pour(locationsDe(p, mois))}<b>{montant(p.montantMinor)}</b>
              <ListeDossiers p={p} />
            </div>
          ))}
        </Bandeau>
      )}

      {e.encaissePlusTard.length > 0 && (
        <Bandeau type="info" titre={t.plusTard.titre(montant(somme(e.encaissePlusTard)))}>
          {e.encaissePlusTard.map((p) => (
            <div key={p.mois} style={{ marginTop: 8 }}>
              {t.encaissesEn(moisLisible(p.mois, mois))}<b>{montant(p.montantMinor)}</b>
              {t.virguleOu(ouEstLArgent(p, mois))}
              <ListeDossiers p={p} />
              {boutons(p.mois)}
            </div>
          ))}
        </Bandeau>
      )}

      {e.surAnnuleesMinor !== 0 && (
        <Bandeau type="info" titre={t.annulee.titre(montant(e.surAnnuleesMinor))}>
          {t.annulee.texte}
        </Bandeau>
      )}
    </div>
  );
}
